import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import CartSidebar from '../components/CartSidebar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { cart, getCartTotal, formatPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    businessName: '',
    city: '',
    phone: '',
    deliveryMethod: 'delivery',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cart.map(item => ({
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        total: formatPrice(getCartTotal()),
      };

      // Save order to backend
      const orderRes = await axios.post(`${API}/orders`, orderData);
      const orderNumber = orderRes?.data?.orderNumber || orderRes?.data?.order?.orderNumber || '';

      // Build WhatsApp message — corto, formato claro, sin asteriscos
      // anidados ni emojis innecesarios para evitar truncado en wa.me.
      const lineas = [];
      lineas.push(`PEDIDO #${orderNumber || ''}`);
      lineas.push(`Negocio: ${formData.businessName || '-'}`);
      lineas.push(`Cliente: ${formData.customerName || '-'}`);
      if (formData.phone) lineas.push(`Tel: ${formData.phone}`);
      lineas.push(`Ciudad: ${formData.city || '-'}`);
      if (formData.deliveryMethod === 'delivery' && formData.address) {
        lineas.push(`Direccion: ${formData.address}`);
      }
      lineas.push(`Entrega: ${formData.deliveryMethod === 'delivery' ? 'Envio a domicilio' : 'Retiro en tienda'}`);
      lineas.push(`-----`);
      cart.forEach(item => {
        lineas.push(`${item.quantity} x ${item.name}`);
      });
      lineas.push(`-----`);
      lineas.push(`TOTAL: ${formatPrice(getCartTotal())}`);
      const message = lineas.join('\n');

      // Tomar el número del depósito desde shipping settings (NO hardcodear).
      // Fallback al número del depósito si no hay setting cargado.
      let storeWhatsapp = '543571522400';
      try {
        const sh = await axios.get(`${API}/shipping-settings`, {
          headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
          params: { _ts: Date.now() },
        });
        const cfg = sh.data?.whatsappNumber;
        if (cfg && /^\d{10,15}$/.test(String(cfg).replace(/\D/g, ''))) {
          storeWhatsapp = String(cfg).replace(/\D/g, '');
        }
      } catch (_) { /* usar fallback */ }
      const whatsappURL = `https://wa.me/${storeWhatsapp}?text=${encodeURIComponent(message)}`;

      // Clear cart and show success
      setSuccess(true);
      clearCart();

      // Redirect to WhatsApp after 2 seconds
      setTimeout(() => {
        window.open(whatsappURL, '_blank');
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error creating order:', error);
      alert('Hubo un error al procesar tu pedido. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-slate-600 mb-4">Tu carrito está vacío</p>
          <button
            onClick={() => navigate('/')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-lg"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center" data-testid="order-success">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={48} className="text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            ¡Pedido Enviado!
          </h2>
          <p className="text-slate-600">Redirigiendo a WhatsApp...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-0">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="checkout-back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }} data-testid="checkout-title">
            Finalizar Pedido
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Form */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              Información del Pedido
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="customerName">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  data-testid="input-customer-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="businessName">
                  Nombre del Negocio *
                </label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  data-testid="input-business-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="city">
                  Ciudad *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  data-testid="input-city"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="phone">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  data-testid="input-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Método de Entrega *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="delivery"
                      checked={formData.deliveryMethod === 'delivery'}
                      onChange={handleChange}
                      className="w-4 h-4 text-emerald-600"
                      data-testid="radio-delivery"
                    />
                    <span className="ml-3 font-medium">Envío a domicilio</span>
                  </label>
                  <label className="flex items-center p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="pickup"
                      checked={formData.deliveryMethod === 'pickup'}
                      onChange={handleChange}
                      className="w-4 h-4 text-emerald-600"
                      data-testid="radio-pickup"
                    />
                    <span className="ml-3 font-medium">Retiro en tienda</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-lg active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-order-button"
              >
                {loading ? 'Procesando...' : 'Enviar Pedido por WhatsApp'}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              Resumen del Pedido
            </h2>
            <div className="space-y-3 mb-6">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between items-start pb-3 border-b border-slate-200" data-testid={`summary-item-${item.id}`}>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{item.name}</p>
                    <p className="text-sm text-slate-600">Cantidad: {item.quantity}</p>
                  </div>
                  <p className="font-bold text-emerald-700">{item.price}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-4 border-t-2 border-slate-300">
              <span className="text-xl font-bold">Total:</span>
              <span className="text-3xl font-bold text-emerald-700" style={{ fontFamily: 'Barlow Condensed' }} data-testid="checkout-total">
                {formatPrice(getCartTotal())}
              </span>
            </div>
          </div>
        </div>
      </main>

      <CartSidebar />
    </div>
  );
};

export default CheckoutPage;
